<template>
  <div class="page-content">
    <div class="headings">
      <h2 class="title title-primary">{{ location }}</h2>
      <span>|</span>
      <h2 class="title title-primary">
        {{ $t("lang.movement.dashboard.page_title_line_1") }}
      </h2>
      <span>|</span>
      <h3 class="title title-primary">
        {{ $t("lang.movement.dashboard.page_title_line_2") }}
      </h3>
    </div>
    <div class="page-header">
      <div class="boxes" v-if="!shouldLoading">
        <bx-box
          dark
          no-padding
          class="col-12 col-lg-3 col-md-4 col-sm-auto p-1"
        >
          <bx-complementary
            v-if="ordersSummary.total != undefined"
            :total="ordersSummary.total"
            :title="$t('lang.movement.dashboard.box_title_1')"
            :left="ordersSummary.left"
            :right="ordersSummary.right"
          />
        </bx-box>

        <bx-box
          dark
          color="outline"
          class="col-12 col-sm-auto p-1 bx-box-adjustable"
        >
          <bx-value
            :label="$t('lang.movement.dashboard.box_label_2')"
            :value="movStats.origin_daily_orders"
            font-size="xs"
          />
        </bx-box>

        <bx-box dark color="outline" class="col-6 col-sm-auto p-1">
          <bx-value
            :label="$t('lang.movement.dashboard.box_label_3')"
            :value="movStats.average_shipping_time"
            font-size="xs"
          />
        </bx-box>

        <bx-box dark color="outline" class="col-6 col-sm-auto p-1">
          <bx-value
            :label="$t('lang.movement.dashboard.box_label_4')"
            :value="movStats.max_shipping_time"
            font-size="xs"
          />
        </bx-box>
      </div>

      <skeleton-boxes progress-box :boxes="3" v-else />
    </div>

    <div class="page-main-content">
      <div class="container-fluid">
        <v-row class="justify-content-end pt-5 pb-0">
          <v-btn
            text
            dark
            class="mb-4"
            @click="deleteOrders"
            v-if="role !== 5 && selectedRows.length"
          >
            <v-avatar color="#27362F" class="pa-2 cursor-pointer">
              <v-icon large color="red"> mdi-delete </v-icon>
            </v-avatar>
          </v-btn>
        </v-row>
        <div class="row">
          <bx-box
            :title="
              ordersCount + ' ' + $t('lang.movement.dashboard.table_title')
            "
            color="white"
            no-padding
            class="col-12"
            :loading="shouldLoading"
          >
            <template v-slot:default>
              <bx-table
                :data="data"
                :columns="columns"
                paginated
                backendPagination
                :currentPage="page"
                :perPage="itemsPerPage"
                :total="ordersCount"
                v-if="!shouldLoading"
                @search="onSearch"
                @update:currentPage="updatePage"
              >
                <template slot-scope="props">
                  <bx-table-column field="destination_status" centered>
                    <bx-order-status
                      :status="props.row.origin_status"
                    ></bx-order-status>
                  </bx-table-column>

                  <bx-table-column field="waiting_time" centered>
                    <strong :class="props.row.time_color">{{
                      props.row.waiting_time
                    }}</strong>
                  </bx-table-column>
                  <bx-table-column centered>
                    <router-link
                      :to="`/movements/exit/order/${props.row.id}/detail`"
                      class="bx-link bx-link-bold"
                      >{{ props.row.id }}</router-link
                    >
                  </bx-table-column>
                  <bx-table-column centered>{{
                    formatDate(props.row.exit_date)
                  }}</bx-table-column>
                  <bx-table-column centered>{{
                    props.row.item_count
                  }}</bx-table-column>
                  <bx-table-column centered>{{
                    props.row.real_item_count
                  }}</bx-table-column>
                  <bx-table-column centered>{{
                    props.row.destination
                  }}</bx-table-column>
                  <bx-table-column centered>
                    <router-link
                      :to="`/movements/exit/order/${props.row.id}/boxes`"
                      class="bx-link bx-link-bold"
                    >
                      <b>{{ props.row.num_verified_boxes }}</b>
                      / {{ props.row.num_boxes }}
                    </router-link>
                  </bx-table-column>
                  <bx-table-column
                    centered
                    class="align-items-start d-flex justify-center"
                  >
                    <v-checkbox
                      class="shrink mr-0 mt-0 pt-0"
                      color="#9ac31c"
                      hide-details
                      :value="props.row.id"
                      v-model="selectedRows"
                    ></v-checkbox>
                  </bx-table-column>
                </template>
              </bx-table>
            </template>
          </bx-box>
          <v-row class="d-flex d-sm-none">
            <div
              v-infinite-scroll="loadMore"
              infinite-scroll-disabled="busy"
              infinite-scroll-distance="20"
              style="width: 100%"
            >
              <v-list width="100%">
                <v-list-item
                  two-line
                  v-for="(item, index) in dataList"
                  :key="index"
                  class="green lighten-5 ma-2 rounded-lg"
                  ripple
                  :to="`/movements/exit/order/${item.id}/detail`"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      <div>
                        {{ item.id }}
                      </div>
                    </v-list-item-title>
                    <v-list-item-subtitle>{{
                      item.destination
                    }}</v-list-item-subtitle>
                    <v-list-item-subtitle class="text-caption">{{
                      formatDate(item.exit_date)
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <div>
                      <img
                        v-if="item.origin_status === 2"
                        style="height: 20px; width: 20px"
                        src="@/assets/i/triangle-outline.svg"
                      />
                      <bx-tag v-if="item.origin_status == 3"
                        ><i class="fa fa-check green-icon"></i
                      ></bx-tag>
                    </div>
                    <v-list-item-subtitle class="text-caption">
                      {{ item.real_item_count + "/" + item.item_count }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle class="text-caption">
                      {{ item.waiting_time }}
                    </v-list-item-subtitle>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </div>
          </v-row>
          <skeleton-table :columns="7" v-if="shouldLoading" />
          <!-- <bx-box
          :title="$t('lang.movement.dashboard.line_graph_title')"
          color="white"
          no-padding
          class="col-12"
          v-if="!shouldLoading && !isMobile"
        >
          <bx-line-chart :chartData="evolutionData" />
        </bx-box> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import c from '../../../colors'
// import months from '../../../months'
import moment from "moment";
import BxTableColumn from "../../bx-components/BxTableColumn";
import common from "@/utils/common";

export default {
  name: "movements-exit-dashboard",
  components: { BxTableColumn },
  mixins: [common],
  data() {
    return {
      key: "movement",
      shouldLoading: true,
      columns: [],
      data: [],
      dataList: [],
      movStats: {},
      ordersCount: 0,
      evolutionData: {},
      ordersSummary: {},
      API_BASE: process.env.VUE_APP_API_BASE,
      reloadInterval: null,
      locationId: null,
      itemsPerPage: process.env.VUE_APP_ITEMS_PER_PAGE,
      page: 1,
      filters: {
        id: null,
        exit_date: null
      },
      selectedRows: []
    };
  },
  computed: {
    location: {
      get() {
        return localStorage.getItem("location");
      },
      set(value) {
        localStorage.setItem("location", value);
      }
    },
    role() {
      return this.$store.state.user.role;
    },
  },
  methods: {
    loadMore() {
      if (
        !this.shouldLoading &&
        this.dataList.length &&
        this.dataList.length < this.ordersCount &&
        this.isMobile
      ) {
        this.page++;
        this.getOrders(this.locationId);
      }
    },
    deleteOrders() {
      this.selectedRows.forEach((x) => {
        let foundIndex = this.data.findIndex((o) => o.id === x);
        this.data.splice(foundIndex, 1);
        this.$api.morders.deleteOrder(x);
      });
      this.selectedRows = [];
    },
    formatDate(date) {
      return date
        ? moment(date).format("DD/ MM/ YYYY, h:mm a")
        : this.$t("lang.messages.no_date_available");
    },
    onSearch(field, value) {
      this.$session.set(this.key + field, value);
      this.filters[field] = value;
      this.getData();
    },
    updatePage(p) {
      this.page = p;
      this.getOrders(this.locationId);
    },
    async getOrders(locationId) {
      try {
        if (this.isMobile) {
          this.shouldLoading = true;
        }
        // Load orders
        const orders = await this.$api.location.movementsExits(locationId, {
          offset: (this.page - 1) * this.itemsPerPage,
          limit: this.itemsPerPage,
          order_code__contains: this.filters.id,
          exit_date__gte:
            this.filters.exit_date && this.filters.exit_date.date_start,
          exit_date__lte:
            this.filters.exit_date && this.filters.exit_date.date_end,
        });

        this.ordersCount = orders.count;
        this.data = orders.results.map(item => {
          let isAfter = moment().isAfter(item.estimated_exit_date);

          return {
            id: item.order_code,
            origin_status: item.origin_status,
            destination_status: item.destination_status,
            waiting_time: item.origin_waiting_time,
            item_count: item.item_count,
            real_item_count: item.origin_real_item_count,
            destination: item.destination_description,
            num_boxes: item.num_boxes,
            num_verified_boxes: item.origin_num_verified_boxes,
            creation_date: item.creation_date,
            exit_date: item.exit_date,
            origin: item.origin,
            time_color: isAfter ? "bx-text-red" : "bx-text-black"
          };
        });

        if (this.isMobile) {
          this.dataList = this.dataList.concat(this.data);
        }

        this.columns = [
          {
            field: "destination_status",
            label: this.$t("lang.movement.dashboard.table_field_1"),
            centered: true,
            width: 80
          },
          {
            field: "waiting_time",
            label: this.$t("lang.movement.dashboard.table_field_2"),
            centered: true,
            width: 130
          },
          {
            field: "id",
            label: this.$t("lang.movement.dashboard.table_field_3"),
            filter: {
              placeholder: this.$t(
                "lang.movement.dashboard.table_field_placeholder_3"
              ),
              value: this.filters.id
            }
          },
          {
            field: "exit_date",
            label: this.$t("lang.movement.dashboard.table_field_4"),
            filter: {
              placeholder: this.$t(
                "lang.movement.dashboard.table_field_placeholder_4"
              ),
              value: this.filters.exit_date,
              isdateRange: true
            },
            centered: true
          },
          {
            field: "item_count",
            label: this.$t("lang.movement.dashboard.table_field_6"),
            centered: true
          },
          {
            field: "real_item_count",
            label: this.$t("lang.movement.dashboard.table_field_5"),
            centered: true
          },
          {
            field: "destination",
            label: this.$t("lang.movement.dashboard.table_field_destination"),
            centered: true
          },
          {
            field: "num_boxes",
            label: this.$t("lang.movement.dashboard.table_field_7"),
            centered: true
          },
          {
            field: "action",
            label: this.$t("lang.inbound.dashboard.table_field_11"),
            centered: true,
          },
        ];
      } catch (error) {
        // console.log(error)
      } finally {
        this.shouldLoading = false;
        this.selectedRows = [];
      }
    },
    async getData() {
      try {
        // Load stats
        // this.location = localStorage.getItem('location')
        this.locationId = localStorage.getItem("location_id");

        if (!location) {
          try {
            let firstLocation = await this.$api.location.search("", {
              limit: 1,
              offset: 0,
              orderBy: "id"
            });
            location = firstLocation.results[0].description;
            this.locationId = firstLocation.results[0].id;
          } catch (error) {
            console.log(error);
          }
        }
        this.movStats = await this.$api.location.movementsStats(
          this.locationId
        );

        // Load orders
        await this.getOrders(this.locationId);
        this.ordersSummary = {
          total: this.movStats.orders,
          left: {
            value: this.movStats.orders - this.movStats.origin_pending_orders,
            label: this.$t("lang.movement.dashboard.box_left_1"),
            icon: require("@/assets/i/ordersTime.png")
          },
          right: {
            value: this.movStats.origin_pending_orders,
            label: this.$t("lang.movement.dashboard.box_right_1"),
            icon: require("@/assets/i/check.png")
          }
        };

        // Evolution data
        // this.evolutionData = {
        //   labels: months,
        //   datasets: [
        //     {
        //       label: this.$t('lang.movement.dashboard.line_graph_label_1'),
        //       backgroundColor: c.getRGBA(c.lime, 0.6),
        //       borderColor: c.lime,

        //       data: this.movStats.current_period_orders
        //     },
        //     {
        //       label: this.$t('lang.movement.dashboard.line_graph_label_2'),
        //       backgroundColor: c.getRGBA(c.blue, 0.6),
        //       borderColor: c.blue,
        //       data: this.movStats.last_period_orders
        //     }
        //   ]
        // }
      } catch (error) {
        // console.log(error)
      } finally {
        this.shouldLoading = false;
      }
    }
  },
  mounted() {
    if (!this.isMobile) {
      this.reloadInterval = setInterval(
        this.getData,
        process.env.VUE_APP_RELOAD_TIME
      );
    }
    this.filters.id = this.$session.get(this.key + "id");
    this.filters.exit_date = this.$session.get(this.key + "exit_date");
    this.getData();
  },
  beforeDestroy() {
    clearInterval(this.reloadInterval);
  }
};
</script>

<style scoped>
@media screen and (max-width: 480px) {
  .bx-box-adjustable >>> .bx-box-content {
    min-height: 109px;
  }

  .bx-box-adjustable >>> .bx-value-value {
    margin-top: 15px;
  }
}
</style>
